/**
 * BoardV3 Sidebar - sticky column labels with resize handle
 * Self-contained: reads all state from boardLayoutStore, manages sidebar resize internally.
 * Includes AddColumnModal.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnLabelsSimple } from './header/ColumnLabelsSimple';
import { ColumnLabelsCollapsed } from './header/ColumnLabelsCollapsed';
import { AddColumnModal } from '../../components/columns/AddColumnModal';
import { ChevronRight } from 'lucide-react';
import { useSidebarWidth, useIsSidebarCollapsed, useIsSidebarResizing, useHeaderHeight, useSidebarControls } from './BoardLayoutContext';
import { useColumnNames, useColumnsActions, useColumns as useColumnDefs } from '../../stores/hooks';

export function BoardV3Sidebar() {
  const { t } = useTranslation();
  const columnLabelWidth = useSidebarWidth();
  const isSidebarCollapsed = useIsSidebarCollapsed();
  const isSidebarResizing = useIsSidebarResizing();
  const headerHeight = useHeaderHeight();

  const sidebar = useSidebarControls();

  const columns = useColumnNames();
  const columnDefinitions = useColumnDefs();
  const { addColumn, updateColumn, deleteColumn: deleteColumnAction, reorderColumns } = useColumnsActions();

  const [showAddColumnModal, setShowAddColumnModal] = useState(false);

  const handleAddColumnClick = useCallback(() => {
    setShowAddColumnModal(true);
  }, []);

  const handleAddColumnSubmit = useCallback((name: string) => {
    addColumn(name);
    setShowAddColumnModal(false);
  }, [addColumn]);

  const handleReorderColumns = useCallback((names: string[]) => {
    const defMap = new Map(columnDefinitions.map(c => [c.name, c]));
    reorderColumns(names.map(n => defMap.get(n) ?? { name: n, defaultCategoryIds: [] }));
  }, [columnDefinitions, reorderColumns]);

  return (
    <>
      <div
        style={{
          position: 'sticky',
          left: 0,
          width: columnLabelWidth,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderRight: '1px solid #e2e8f0',
          zIndex: 25,
        }}
      >
        {/* Corner Cell */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            width: columnLabelWidth,
            height: headerHeight,
            backgroundColor: '#fff',
            borderBottom: '1px solid #e2e8f0',
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isSidebarCollapsed ? 'pointer' : undefined,
          }}
          onClick={isSidebarCollapsed ? sidebar.toggleCollapse : undefined}
          title={isSidebarCollapsed ? t('boardv3.sidebar.expand') : undefined}
        >
          {isSidebarCollapsed ? (
            <ChevronRight size={12} style={{ color: '#64748b' }} />
          ) : (
            <span style={{ fontSize: 10, fontWeight: 400, color: '#64748b' }}>
              Columns
            </span>
          )}
        </div>

        {/* Column Labels */}
        {isSidebarCollapsed ? (
          <ColumnLabelsCollapsed />
        ) : (
          <ColumnLabelsSimple
            onAddColumn={handleAddColumnClick}
            onReorderColumns={handleReorderColumns}
            onUpdateColumn={updateColumn}
            onDeleteColumn={deleteColumnAction}
          />
        )}

        {/* Sidebar Resize Handle */}
        <div
          onMouseDown={sidebar.handleResizeStart}
          onDoubleClick={sidebar.handleDoubleClick}
          style={{
            position: 'absolute',
            top: 0,
            right: -2,
            width: 4,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 31,
            backgroundColor: isSidebarResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!isSidebarResizing) {
              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSidebarResizing) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        />
      </div>

      <AddColumnModal
        isOpen={showAddColumnModal}
        onClose={() => setShowAddColumnModal(false)}
        onAdd={handleAddColumnSubmit}
        existingColumns={columns}
      />
    </>
  );
}
